// ── CASH AND SF-425 (Award Setup tab) ────────────────────────
// Log each drawdown, see federal cash on hand against the actuals imported on
// the Budget vs Actual tab, and build an SF-425 worksheet for a period end.
// Every figure is computed on the server (lib/cash.js).
import { useState, useEffect, useCallback } from "react";
import { FONT_H, FONT_UI, FONT_MONO } from "../theme";
import { API, apiFetch } from "../utils/api";

const RED = "#dc2626", AMBER = "#d97706";
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const BLANK = { requested: "", received: "", amount: "", periodFrom: "", periodTo: "", system: "PMS", reference: "", note: "" };
const INPUT_SOURCES = ["Your input", "Enter this yourself"];

function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function CashTab({ T, card, label, input, btn, award }) {
  const [awards, setAwards]   = useState([]);
  const [awardId, setAwardId] = useState(award?.id || "");
  const [data, setData]       = useState(null);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [draft, setDraft]     = useState(BLANK);
  const [costShare, setCostShare] = useState("");
  const [ws, setWs]           = useState({ end: "", unliquidated: "", indirectBase: "", indirectType: "" });
  const [sheet, setSheet]     = useState(null);

  useEffect(() => {
    apiFetch(`${API}/setup/awards`).then((r) => r.json()).then((d) => {
      const list = d.records || [];
      setAwards(list);
      setAwardId((id) => id || list[0]?.id || "");
    }).catch(() => {});
  }, []);

  const show = (d) => { setData(d); setCostShare(String(d.cash.costShareRequired || "")); setSheet(null); };
  const load = useCallback(async () => {
    if (!awardId) return;
    setError("");
    try {
      const res = await apiFetch(`${API}/cash/${encodeURIComponent(awardId)}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not load the drawdown log.");
      show(d);
    } catch (e) { setError(e.message); setData(null); }
  }, [awardId]);
  useEffect(() => { load(); }, [load]);

  const save = async (draws, costShareRequired = costShare) => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/cash/${encodeURIComponent(awardId)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ draws, costShareRequired }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not save.");
      show(d);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setBusy(false); }
  };

  const query = (extra = "") => `${API}/cash/${encodeURIComponent(awardId)}/sf425?${new URLSearchParams(ws)}${extra}`;
  const worksheet = async () => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(query("&format=json"));
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not build the worksheet.");
      setSheet(d);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const download = async () => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(query());
      if (!res.ok) throw new Error((await res.json()).error || "Could not build the worksheet.");
      saveFile(await res.blob(), `sf425-worksheet-${ws.end}.xlsx`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  if (!awards.length) {
    return <div style={{ ...card, fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted }}>Save an award on the Notice of Award tab first.</div>;
  }

  const draws = data?.cash.draws || [];
  const p = data?.position;
  const th = { textAlign: "left", padding: "8px 10px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "9px 10px", fontFamily: FONT_UI, fontSize: 13, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
  const h2 = { fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 4px" };
  const note = { fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, margin: "4px 0 12px" };
  const field = (k, l, type = "text", w = 150) => (
    <label key={k} style={{ width: w }}><span style={label}>{l}</span>
      <input style={input} type={type} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
    </label>
  );

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={card}>
        <label style={{ display: "block", maxWidth: 420 }}><span style={label}>Award</span>
          <select style={input} value={awardId} onChange={(e) => setAwardId(e.target.value)}>
            {awards.map((a) => <option key={a.id} value={a.id}>{[a.awardNumber, a.projectTitle].filter(Boolean).join(" · ") || a.id}</option>)}
          </select>
        </label>
        {error && <div role="alert" style={{ marginTop: 12, color: RED, fontFamily: FONT_UI, fontSize: 13 }}>{error}</div>}
      </div>

      {p && (
        <div style={card}>
          <h2 style={h2}>Cash position</h2>
          <p style={note}>
            As of {p.asOf}. Disbursements are the federal share ({p.federalSharePct}%) of actuals imported{p.latestMonth ? ` through ${p.latestMonth}` : ""}.
          </p>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", fontFamily: FONT_UI }}>
            {[["Cash received", money(p.receipts)], ["Disbursed", money(p.disbursements)], ["Cash on hand", money(p.cashOnHand)],
              ["Average daily disbursement", money(p.avgDaily)], ["Days of cash on hand", p.daysOnHand ?? "—"]].map(([l, v]) => (
              <div key={l}><span style={label}>{l}</span>
                <div style={{ fontSize: 18, fontWeight: 700, color: l === "Days of cash on hand" && p.excess ? RED : T.text }}>{v}</div>
              </div>
            ))}
          </div>
          {!!p.warnings.length && (
            <div style={{ marginTop: 14, paddingLeft: 12, borderLeft: `3px solid ${AMBER}` }}>
              {p.warnings.map((w) => <p key={w} style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, margin: "4px 0", lineHeight: 1.5 }}>{w}</p>)}
            </div>
          )}
        </div>
      )}

      {data && (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <div style={{ padding: "16px 20px 6px" }}>
            <h2 style={h2}>Drawdown log</h2>
            <p style={note}>Enter a refund to the agency as a negative amount. Add the date received when the cash arrives.</p>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Requested", "Received", "Amount", "Covers", "System", "Reference", "Note", ""].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {draws.map((d) => (
                <tr key={d.id}>
                  <td style={td}>{d.requested}</td>
                  <td style={td}>{d.received || (
                    <input type="date" style={{ ...input, width: 150 }} aria-label={`Date received for the draw requested ${d.requested}`} disabled={busy}
                      onChange={(e) => e.target.value && save(draws.map((x) => (x.id === d.id ? { ...x, received: e.target.value } : x)))} />
                  )}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{money(d.amount)}</td>
                  <td style={td}>{d.periodFrom || d.periodTo ? `${d.periodFrom || "?"} to ${d.periodTo || "?"}` : ""}</td>
                  <td style={td}>{d.system}</td>
                  <td style={td}>{d.reference}</td>
                  <td style={{ ...td, whiteSpace: "normal" }}>{d.note}</td>
                  <td style={td}>
                    <button style={btn(false)} disabled={busy} onClick={() => window.confirm(`Remove the ${money(d.amount)} draw requested ${d.requested}?`) && save(draws.filter((x) => x.id !== d.id))}>Remove</button>
                  </td>
                </tr>
              ))}
              {!draws.length && <tr><td style={{ ...td, color: T.textMuted }} colSpan={8}>No draws logged yet.</td></tr>}
            </tbody>
          </table>
          <div style={{ padding: "14px 20px 18px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            {field("requested", "Date requested", "date")}
            {field("received", "Date received", "date")}
            {field("amount", "Amount", "number", 140)}
            {field("periodFrom", "Covers from", "date")}
            {field("periodTo", "Covers to", "date")}
            <label style={{ width: 130 }}><span style={label}>System</span>
              <input style={input} list="draw-systems" value={draft.system} onChange={(e) => setDraft({ ...draft, system: e.target.value })} />
              <datalist id="draw-systems"><option value="PMS" /><option value="ASAP" /></datalist>
            </label>
            {field("reference", "Reference number")}
            {field("note", "Note", "text", 260)}
            <button style={btn(true)} disabled={busy || !draft.requested || !draft.amount}
              onClick={async () => { if (await save([...draws, draft])) setDraft(BLANK); }}>Add draw</button>
          </div>
          <div style={{ padding: "0 20px 18px", display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <label style={{ width: 200 }}><span style={label}>Cost share required</span>
              <input style={input} type="number" min="0" value={costShare} onChange={(e) => setCostShare(e.target.value)} />
            </label>
            <button style={btn(false)} disabled={busy || String(data.cash.costShareRequired || "") === costShare} onClick={() => save(draws)}>Save cost share</button>
            <span style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted }}>Leave at 0 if the award has no cost share. Expenditures are then all federal.</span>
          </div>
        </div>
      )}

      {data && (
        <div style={card}>
          <h2 style={h2}>SF-425 worksheet</h2>
          <p style={note}>A worksheet for filling in the official Federal Financial Report (2 CFR 200.328). It is not the form itself.</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            {[["end", "Reporting period end", "date"], ["unliquidated", "10f Unliquidated obligations", "number"], ["indirectBase", "11d Indirect base (if not TDC)", "number"], ["indirectType", "11a Indirect type", "text"]].map(([k, l, type]) => (
              <label key={k} style={{ width: 200 }}><span style={label}>{l}</span>
                <input style={input} type={type} value={ws[k]} placeholder={k === "indirectType" ? "e.g. Provisional" : ""} onChange={(e) => setWs({ ...ws, [k]: e.target.value })} />
              </label>
            ))}
            <button style={btn(false)} disabled={busy || !ws.end} onClick={worksheet}>Show worksheet</button>
            <button style={btn(true)} disabled={busy || !ws.end} onClick={download}>Download Excel</button>
          </div>
          {sheet && (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
                <thead><tr>{["Line", "Item", "Amount", "Where it comes from"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {sheet.lines.map((l) => (
                    <tr key={l.line}>
                      <td style={{ ...td, fontFamily: FONT_MONO }}>{l.line}</td>
                      <td style={td}>{l.label}{l.note && <div style={{ fontSize: 12, color: T.textMuted }}>{l.note}</div>}</td>
                      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{l.text ? (l.value ?? "—") : money(l.value)}</td>
                      <td style={{ ...td, color: INPUT_SOURCES.includes(l.source) ? AMBER : T.textMuted }}>{l.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 14, paddingLeft: 12, borderLeft: `3px solid ${AMBER}` }}>
                <span style={label}>Check before filing</span>
                {sheet.warnings.map((w) => <p key={w} style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, margin: "4px 0", lineHeight: 1.5 }}>{w}</p>)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
